import { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const ROLE_CONFIG = {
  super_admin:  { label: 'Super Admin',          colour: 'C9A84C', bg: 'FFF8E7' },
  admin:        { label: 'Admin',                colour: '1A4035', bg: 'EEF7F2' },
  head_of_bids: { label: 'Head of Bid Mgmt',     colour: '2D6A4F', bg: 'D4EAE0' },
  bid_lead:     { label: 'Bid Lead',             colour: '2B6CB0', bg: 'EBF8FF' },
  contributor:  { label: 'Contributor',          colour: '4A5568', bg: 'F7FAFC' },
  reviewer:     { label: 'Reviewer',             colour: '718096', bg: 'F7FAFC' },
}

function UserManagement({ session, profile, onNavigate }) {
  const [users, setUsers] = useState([])
  const [invitations, setInvitations] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('bid_lead')
  const [inviting, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [copiedToken, setCopiedToken] = useState(null)

  const isAdmin = profile?.role === 'super_admin' || profile?.role === 'admin'
  const orgId = profile?.organisation_id

  useEffect(() => {
    if (orgId) {
      loadUsers()
      loadInvitations()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  const loadUsers = async () => {
    setLoading(true)
    const { data } = await supabase
      .from('profiles')
      .select('id, username, full_name, role, is_active, created_at')
      .eq('organisation_id', orgId)
      .order('created_at')
    setUsers(data || [])
    setLoading(false)
  }

  const loadInvitations = async () => {
    const { data } = await supabase
      .from('invitations')
      .select('*')
      .eq('organisation_id', orgId)
      .eq('accepted', false)
      .gte('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
    setInvitations(data || [])
  }

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return
    setSaving(true)
    setError(null)
    setSuccess(null)

    // Check seat capacity
    const { data: orgData } = await supabase
      .from('organisations')
      .select('licensed_user_count')
      .eq('id', orgId)
      .single()

    const { count: activeCount } = await supabase
      .from('profiles')
      .select('id', { count: 'exact', head: true })
      .eq('organisation_id', orgId)
      .eq('is_active', true)

    if (orgData && activeCount >= orgData.licensed_user_count) {
      setError(`You have used all ${orgData.licensed_user_count} licensed seats. Contact Captus at hello@captus.co.uk to add more capacity before inviting new users.`)
      setSaving(false)
      return
    }

    const { data, error } = await supabase
      .from('invitations')
      .insert([{
        organisation_id: orgId,
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole,
        invited_by: session.user.id
      }])
      .select()
      .single()

    setSaving(false)

    if (error) {
      setError(error.message)
      return
    }

    setSuccess(`Invitation created for ${inviteEmail}. Share the invitation code below with them.`)
    setInviteEmail('')
    setInvitations(prev => [data, ...prev])
    setShowInvite(false)
  }

  const handleRoleChange = async (userId, newRole) => {
    await supabase
      .from('profiles')
      .update({ role: newRole })
      .eq('id', userId)
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, role: newRole } : u))
  }

  const handleToggleActive = async (userId, currentActive) => {
    await supabase
      .from('profiles')
      .update({ is_active: !currentActive })
      .eq('id', userId)
    setUsers(prev => prev.map(u => u.id === userId ? { ...u, is_active: !currentActive } : u))
  }

  const handleCopyToken = (token) => {
    navigator.clipboard.writeText(token)
    setCopiedToken(token)
    setTimeout(() => setCopiedToken(null), 2000)
  }

  const handleRevokeInvitation = async (id) => {
    await supabase.from('invitations').delete().eq('id', id)
    setInvitations(prev => prev.filter(i => i.id !== id))
  }

  const availableRoles = profile?.role === 'super_admin'
    ? Object.keys(ROLE_CONFIG)
    : Object.keys(ROLE_CONFIG).filter(r => r !== 'super_admin')

  return (
    <div className="scorecard-page container">
      <div className="page-header">
        <p className="label">Organisation</p>
        <h1>User Management</h1>
        <p>
          Manage users in {profile?.organisations?.name || 'your organisation'}.
          Invite new members and manage their roles and access.
        </p>
      </div>

      {error && <div className="error-msg">{error}</div>}
      {success && <div className="success-msg">{success}</div>}

      {/* Current users */}
      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card__header">
          <span className="card__title">Team Members</span>
          {isAdmin && (
            <button
              className="btn btn--primary"
              onClick={() => setShowInvite(!showInvite)}
              style={{ padding: '6px 16px', fontSize: '0.8125rem' }}
            >
              + Invite User
            </button>
          )}
        </div>

        {/* Invite form */}
        {showInvite && (
          <div style={{
            background: 'var(--green-pale)', padding: '20px',
            marginBottom: '20px', borderLeft: '3px solid var(--green)'
          }}>
            <div style={{
              fontFamily: 'Cormorant Garamond, serif',
              fontSize: '1.125rem', fontWeight: 600,
              color: 'var(--green-dark)', marginBottom: '16px'
            }}>
              Invite a New User
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder="colleague@organisation.com"
                  autoFocus
                />
              </div>
              <div className="form-group">
                <label>Role *</label>
                <select
                  value={inviteRole}
                  onChange={e => setInviteRole(e.target.value)}
                  style={{ width: '100%' }}
                >
                  {availableRoles.map(role => (
                    <option key={role} value={role}>
                      {ROLE_CONFIG[role].label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--slate)', marginBottom: '16px' }}>
              An invitation code will be generated. Share it with the user — they will need it when they sign up for Cadence.
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                className="btn btn--primary"
                onClick={handleInvite}
                disabled={inviting || !inviteEmail.trim()}
              >
                {inviting ? 'Creating...' : 'Generate Invitation'}
              </button>
              <button
                className="btn btn--secondary"
                onClick={() => setShowInvite(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading && <p style={{ color: 'var(--slate)' }}>Loading users...</p>}

        {users.map(user => {
          const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.contributor
          const isSelf = user.id === session.user.id
          return (
            <div
              key={user.id}
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', padding: '14px 0',
                borderBottom: '1px solid var(--green-light)',
                opacity: user.is_active ? 1 : 0.5
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  gap: '10px', marginBottom: '4px'
                }}>
                  <span style={{
                    fontWeight: 600, fontSize: '0.9375rem',
                    color: 'var(--charcoal)'
                  }}>
                    {user.full_name || user.username}
                  </span>
                  {isSelf && (
                    <span style={{
                      fontSize: '0.625rem', fontWeight: 600,
                      color: 'var(--green)', background: 'var(--green-pale)',
                      padding: '2px 6px'
                    }}>
                      You
                    </span>
                  )}
                  {!user.is_active && (
                    <span style={{
                      fontSize: '0.625rem', fontWeight: 600,
                      color: 'var(--red)', background: '#FFF5F5',
                      padding: '2px 6px'
                    }}>
                      Inactive
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--slate)' }}>
                  @{user.username}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* Role selector */}
                {isAdmin && !isSelf ? (
                  <select
                    value={user.role}
                    onChange={e => handleRoleChange(user.id, e.target.value)}
                    style={{
                      padding: '5px 10px',
                      border: `1px solid #${roleConfig.colour}`,
                      fontSize: '0.8125rem', fontWeight: 600,
                      color: `#${roleConfig.colour}`,
                      background: `#${roleConfig.bg}`,
                      borderRadius: 0, outline: 'none', cursor: 'pointer'
                    }}
                  >
                    {availableRoles.map(role => (
                      <option key={role} value={role}>
                        {ROLE_CONFIG[role].label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span style={{
                    fontSize: '0.75rem', fontWeight: 700,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                    color: `#${roleConfig.colour}`,
                    background: `#${roleConfig.bg}`,
                    padding: '4px 10px'
                  }}>
                    {roleConfig.label}
                  </span>
                )}

                {/* Activate / Deactivate */}
                {isAdmin && !isSelf && (
                  <button
                    onClick={() => handleToggleActive(user.id, user.is_active)}
                    style={{
                      background: 'none',
                      border: `1px solid ${user.is_active ? 'var(--silver)' : 'var(--green)'}`,
                      color: user.is_active ? 'var(--slate)' : 'var(--green)',
                      padding: '4px 10px', fontSize: '0.75rem',
                      fontWeight: 600, cursor: 'pointer'
                    }}
                  >
                    {user.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* Pending invitations */}
      {isAdmin && invitations.length > 0 && (
        <div className="card" style={{ marginBottom: '24px' }}>
          <div className="card__header">
            <span className="card__title">Pending Invitations</span>
            <span className="card__weight">{invitations.length} pending</span>
          </div>
          {invitations.map(invite => (
            <div
              key={invite.id}
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', padding: '12px 0',
                borderBottom: '1px solid var(--green-light)'
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.9375rem', color: 'var(--charcoal)', marginBottom: '2px' }}>
                  {invite.email}
                </div>
                <div style={{ fontSize: '0.8125rem', color: 'var(--slate)' }}>
                  {ROLE_CONFIG[invite.role]?.label} · Expires {new Date(invite.expires_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{
                  fontFamily: 'monospace', fontSize: '0.75rem',
                  color: 'var(--slate)', background: 'var(--off-white)',
                  padding: '4px 10px', border: '1px solid var(--silver)',
                  maxWidth: '200px', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>
                  {invite.token}
                </div>
                <button
                  onClick={() => handleCopyToken(invite.token)}
                  style={{
                    background: copiedToken === invite.token ? 'var(--green)' : 'none',
                    border: `1px solid ${copiedToken === invite.token ? 'var(--green)' : 'var(--silver)'}`,
                    color: copiedToken === invite.token ? 'var(--white)' : 'var(--slate)',
                    padding: '4px 10px', fontSize: '0.75rem',
                    fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s'
                  }}
                >
                  {copiedToken === invite.token ? 'Copied!' : 'Copy Code'}
                </button>
                <button
                  onClick={() => handleRevokeInvitation(invite.id)}
                  style={{
                    background: 'none', border: '1px solid var(--silver)',
                    color: 'var(--slate)', padding: '4px 8px',
                    fontSize: '0.75rem', cursor: 'pointer'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--red)'; e.currentTarget.style.color = 'var(--red)' }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--silver)'; e.currentTarget.style.color = 'var(--slate)' }}
                >
                  Revoke
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="actions">
        <button className="btn btn--secondary" onClick={() => onNavigate('dashboard')}>
          ← Dashboard
        </button>
      </div>
    </div>
  )
}

export default UserManagement